import React, { createContext, useCallback, useContext, useMemo, useState } from "react"
import type { DayWisePricingGroupLocal } from "../component/hotelContract/hotelContractTypes"

export type HotelDayWisePricingSlice = {
    flag_day_wise_pricing: boolean
    dayWiseGroups: DayWisePricingGroupLocal[]
}

type HotelDayWisePricingContextValue = {
    /** Reactive read — prefer this in useMemo deps over getSlice. */
    slices: Record<string, HotelDayWisePricingSlice>
    getSlice: (scopeId: string) => HotelDayWisePricingSlice | undefined
    setSlice: (scopeId: string, slice: HotelDayWisePricingSlice) => void
    patchSlice: (scopeId: string, updater: (prev: HotelDayWisePricingSlice | undefined) => HotelDayWisePricingSlice) => void
    /** Replace or remove entry (e.g. after contract delete). */
    removeSlice: (scopeId: string) => void
}

const HotelDayWisePricingContext = createContext<HotelDayWisePricingContextValue | null>(null)

export const HotelDayWisePricingProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
    const [slices, setSlices] = useState<Record<string, HotelDayWisePricingSlice>>({})

    const getSlice = useCallback((scopeId: string) => slices[scopeId], [slices])

    const setSlice = useCallback((scopeId: string, slice: HotelDayWisePricingSlice) => {
        setSlices((prev) => ({ ...prev, [scopeId]: slice }))
    }, [])

    const patchSlice = useCallback((scopeId: string, updater: (prev: HotelDayWisePricingSlice | undefined) => HotelDayWisePricingSlice) => {
        setSlices((prev) => {
            const next = updater(prev[scopeId])
            return { ...prev, [scopeId]: next }
        })
    }, [])

    const removeSlice = useCallback((scopeId: string) => {
        setSlices((prev) => {
            const { [scopeId]: _, ...rest } = prev
            return rest
        })
    }, [])

    const value = useMemo(
        () => ({ slices, getSlice, setSlice, patchSlice, removeSlice }),
        [slices, getSlice, setSlice, patchSlice, removeSlice],
    )

    return <HotelDayWisePricingContext.Provider value={value}>{children}</HotelDayWisePricingContext.Provider>
}

export function useHotelDayWisePricingContext(): HotelDayWisePricingContextValue {
    const ctx = useContext(HotelDayWisePricingContext)
    if (!ctx) {
        throw new Error("useHotelDayWisePricingContext must be used within HotelDayWisePricingProvider")
    }
    return ctx
}
